import { useState } from "react";
import { Copy, Download, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { copyToClipboard, downloadFile } from "@/lib/utils";
import { Button } from "./Button";

export function SecretField({
  label,
  value,
  revealDefault = false,
  download,
  multiline = false,
  className,
}: {
  label: string;
  value: string;
  revealDefault?: boolean;
  download?: { filename: string; mime?: string };
  multiline?: boolean;
  className?: string;
}) {
  const [revealed, setRevealed] = useState(revealDefault);

  async function onCopy() {
    await copyToClipboard(value);
    toast.success(`${label} copied`);
  }

  function onDownload() {
    if (!download) return;
    downloadFile(download.filename, value, download.mime ?? "text/plain");
    toast.success(`Downloaded ${download.filename}`);
  }

  const masked = value.replace(/./g, "•");

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Hide" : "Reveal"}
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCopy} aria-label="Copy">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          {download && (
            <Button variant="ghost" size="sm" onClick={onDownload} aria-label="Download">
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {multiline ? (
        <pre className="block w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
          {revealed ? value : masked}
        </pre>
      ) : (
        <div className="block w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs font-mono break-all">
          {revealed ? value : masked}
        </div>
      )}
    </div>
  );
}
